import { useTableFilter } from "@/shared";
import { GeneralTable } from "@/shared/components/ui";
import DateRangePicker from "@/shared/components/ui/CustomSearch/DateRangePicker";
import {
  Grid,
  MenuItem,
  Box,
  Button,
  Alert,
  Typography,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Select,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useRef, useMemo, useState, useEffect, useCallback } from "react";
import Flatpickr from "react-flatpickr";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import { Description, PictureAsPdf, Search, Refresh } from "@mui/icons-material";
import { useObtenerReporteGeneralVentas, useObtenerFiltrosVentas } from "@/shared/actions/erp";
import { useTableColumnsReporteVenta } from "@/shared/hooks/useTableColumnsReporteVenta";
import { format } from "date-fns";
import { exportToExcel, exportToPDF, ExportColumn } from "../utils/exportUtils";

const PLAN_OPTIONS = ["Home", "Corporativo", "Pymes", "ISP"];

export default function TableReporteVenta() {
  const {
    dateBefore,
    dateAfter,
    onChangeRangeDate,
    cleanFilter,
    vendedor,
  } = useTableFilter();

  // Estado de filtros
  const [planFilter, setPlanFilter] = useState<string[]>([]);
  const [canalFilter, setCanalFilter] = useState<string[]>([]);

  // Estados para búsqueda en selector de canal y plan
  const [searchCanal, setSearchCanal] = useState<string>("");
  const [searchPlan, setSearchPlan] = useState<string>("");

  // Estados de datos y carga
  const [allData, setAllData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [allowedDateRange, setAllowedDateRange] = useState<{ start: string; end: string } | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string>("");

  // Estados para modal de exportación
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportType, setExportType] = useState<"excel" | "pdf" | "">("");
  const [exportProgress, setExportProgress] = useState<number>(0);

  // Paginación para Material React Table (manejada internamente por la tabla)
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Calcular fechas del mes anterior
  const { firstDayPrevMonth, lastDayPrevMonth } = useMemo(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-11

    // Primer día del mes anterior
    const firstDay = new Date(year, month - 1, 1);

    // Último día del mes anterior (día 0 del mes actual)
    const lastDay = new Date(year, month, 0);

    return {
      firstDayPrevMonth: format(firstDay, "yyyy-MM-dd"),
      lastDayPrevMonth: format(lastDay, "yyyy-MM-dd")
    };
  }, []);

  // AbortController para cancelar requests anteriores
  const abortControllerRef = useRef<AbortController | null>(null);

  // Hook para obtener datos
  const obtenerReporteMutation = useObtenerReporteGeneralVentas();

  // Hook para obtener filtros de canales
  const { data: filtrosData, isLoading: isLoadingFiltros, refetch: refetchFiltros } = useObtenerFiltrosVentas();

  // Cargar datos con filtros
  const loadData = useCallback(async () => {
    // Cancelar request anterior si existe
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError("");

    try {
      const filters: any = {
        fecha_desde: dateAfter || firstDayPrevMonth,
        fecha_hasta: dateBefore || lastDayPrevMonth,
      };

      if (vendedor) filters.id_usuario = parseInt(vendedor);

      const response = await obtenerReporteMutation.mutateAsync(filters);

      // Verificar si fue abortado
      if (abortControllerRef.current?.signal.aborted) {
        return;
      }

      if (response.data && Array.isArray(response.data)) {
        setAllData(response.data);
        // Resetear a la primera página cuando se cargan nuevos datos
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      } else {
        setError("Estructura de respuesta inesperada");
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
        return; // Request cancelado, no mostrar error
      }

      // Manejar error de rango de fechas inválido
      if (err?.response?.data?.code === 'INVALID_DATE_RANGE') {
        const allowed = err.response.data.allowed_range;
        setAllowedDateRange(allowed);
        setError(err.response.data.message || "Rango de fechas inválido");
      } else {
        setError(err?.message || "Error al cargar datos");
      }
    } finally {
      setIsLoading(false);
    }
  }, [dateAfter, dateBefore, vendedor, firstDayPrevMonth, lastDayPrevMonth]);

  // Cargar datos al montar (con rango del mes anterior)
  useEffect(() => {
    if (!dateAfter && !dateBefore) {
      // Establecer rango del mes anterior por defecto
      const startDate = new Date(firstDayPrevMonth);
      const endDate = new Date(lastDayPrevMonth);
      onChangeRangeDate([startDate, endDate]);
    }
  }, []);

  // Validar rango de fechas
  useEffect(() => {
    if (!dateAfter || !dateBefore) {
      setDateRangeError("");
      return;
    }

    const fechaDesde = new Date(dateAfter);
    const fechaHasta = new Date(dateBefore);
    const diffDays = Math.ceil((fechaHasta.getTime() - fechaDesde.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    if (diffDays > 31) {
      setDateRangeError(`El rango seleccionado es de ${diffDays} días. Solo se permiten hasta 31 días.`);
      setAllData([]);
      return;
    } else {
      setDateRangeError("");
    }
  }, [dateAfter, dateBefore]);

  // Cargar datos cuando cambian los filtros (con debounce) - DESHABILITADO
  // useEffect(() => {
  //   if (!dateAfter || !dateBefore || dateRangeError || !hasUserInteracted) return;

  //   const timeoutId = setTimeout(() => {
  //     loadData();
  //   }, 500); // Debounce de 500ms

  //   return () => clearTimeout(timeoutId);
  // }, [dateAfter, dateBefore, vendedor, dateRangeError, hasUserInteracted]);

  const { columnBase } = useTableColumnsReporteVenta({ pagination });
  const flatpickrRef = useRef<Flatpickr>(null);

  // Obtener lista de canales disponibles desde el endpoint
  const canalesDisponibles = useMemo(() => {
    if (!filtrosData?.data) return [];
    return (filtrosData.data as any).canales || [];
  }, [filtrosData]);

  // Filtrar canales por búsqueda
  const canalesFiltrados = useMemo(() => {
    if (!searchCanal) return canalesDisponibles;
    return canalesDisponibles.filter((canal: string) =>
      canal.toLowerCase().includes(searchCanal.toLowerCase())
    );
  }, [canalesDisponibles, searchCanal]);

  // Filtrar planes por búsqueda
  const planesFiltrados = useMemo(() => {
    if (!searchPlan) return PLAN_OPTIONS;
    return PLAN_OPTIONS.filter((plan: string) =>
      plan.toLowerCase().includes(searchPlan.toLowerCase())
    );
  }, [searchPlan]);

  // Filtrar datos del lado del cliente por tipo_plan y canal
  const filteredData = useMemo(() => {
    let data = allData;

    // Filtrar automáticamente por estado_instalacion === 'OK' o 'SOLUCIONADO'
    data = data.filter(item => {
      const estadoInstalacion = String(item.estado_instalacion || "").trim().toUpperCase();
      return estadoInstalacion === "OK" || estadoInstalacion === "SOLUCIONADO";
    });

    // Filtrar por canal
    if (canalFilter.length > 0) {
      // Filtrar valores válidos
      const canalesValidos = canalFilter.filter(c => c && c.trim());
      if (canalesValidos.length > 0) {
        data = data.filter(item => {
          const canalItem = item.cargo || "";
          const canalNormalizado = canalItem.charAt(0).toUpperCase() + canalItem.slice(1).toLowerCase();
          return canalesValidos.some(canal =>
            canal && (
              canalNormalizado.includes(canal) ||
              canal.includes(canalNormalizado) ||
              String(item.cargo || "").toUpperCase().includes(canal.toUpperCase())
            )
          );
        });
      }
    }

    // Filtrar por tipo_plan
    if (planFilter.length > 0) {
      // Filtrar valores válidos
      const planesValidos = planFilter.filter(p => p && p.trim());
      if (planesValidos.length > 0) {
        data = data.filter(item =>
          planesValidos.some(plan =>
            plan && String(item.tipo_plan || "").toUpperCase().includes(plan.toUpperCase())
          )
        );
      }
    }

    return data;
  }, [allData, canalFilter, planFilter]);

  const exportFilters = useMemo(() => {
    const filters: Record<string, string> = {};

    if (dateAfter && dateBefore) {
      filters["Rango de fechas"] = `${dateAfter} al ${dateBefore}`;
    }

    if (canalFilter.length > 0) {
      filters["Canales"] = canalFilter.join(", ");
    }

    if (planFilter.length > 0) {
      filters["Tipo de plan"] = planFilter.join(", ");
    }

    return filters;
  }, [
    dateAfter,
    dateBefore,
    canalFilter,
    planFilter,
  ]);

  // Paginación en memoria optimizada - solo pasamos las filas visibles a la tabla
  // Esto mejora drásticamente el rendimiento porque GeneralTable solo renderiza 10-50 filas en vez de 4812
  const paginatedData = useMemo(() => {
    const startIdx = pagination.pageIndex * pagination.pageSize;
    const endIdx = startIdx + pagination.pageSize;
    return filteredData.slice(startIdx, endIdx);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleCleanFilter = () => {
    setPlanFilter([]);
    setCanalFilter([]);
    cleanFilter();

    // Restablecer rango del mes anterior
    const startDate = new Date(firstDayPrevMonth);
    const endDate = new Date(lastDayPrevMonth);
    onChangeRangeDate([startDate, endDate]);
  };

  const handleSearch = async () => {
    await loadData();
  };

  const handleRefreshFiltros = async () => {
    await refetchFiltros();
  };

  // Exportar a Excel
  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportType("excel");
    setExportProgress(0);

    try {
      // Simulación de progreso
      setExportProgress(30);

      const columns: ExportColumn[] = [
        { header: "#", key: "clave" },
        { header: "Tipo", key: "tipo" },
        { header: "Vendedor", key: "vendedor" },
        { header: "Area Ventas", key: "cargo" },
        { header: "Fecha Instalación", key: "fecha" },
        { header: "Cliente", key: "nombre_cliente" },
        { header: "Ciudad", key: "ciudad" },
        { header: "Sector", key: "sector" },
        { header: "Zona", key: "zona" },
        { header: "Coordenadas", key: "coordenadas" },
        { header: "Tecnico", key: "tecnico" },
        { header: "Forma Pago", key: "forma_pago" },
        { header: "Banco", key: "Banco" },
        { header: "Estado Venta", key: "estado_venta" },
        { header: "Estado Instalacion", key: "estado_instalacion" },
        { header: "Plan Comercial", key: "nombre_plan_factura" },
        { header: "Plan Tecnico", key: "nombre_plan" },
        { header: "Tipo Plan", key: "tipo_plan" },
        { header: "Costo Mensual", key: "valor_mensual" },
        { header: "Costo Instalación", key: "valor_instalacion" },
        { header: "Servicio Actual", key: "tipo_servicio" },
        { header: "Obs. Adicional", key: "tipo_instalacion" },
        { header: "Equipo", key: "equipo" },
        { header: "Usuario Revisa", key: "usuario_revisa" },
        { header: "Buro", key: "buro" },
        { header: "Segmentación", key: "buro" },
        { header: "Estado Contrato", key: "estado_contrato" },
        { header: "Licenciamiento", key: "licenciamiento" },
        { header: "Medio Publicitario", key: "medio" },
        { header: "Herramienta Utilizada", key: "medio" },
        { header: "Existe Router AC", key: "router_adicional" },
        { header: "Utp Utilizado", key: "detalle_utp" },
        { header: "Promocion Aplicada", key: "detalle_promocion" },
        { header: "Promocion Referido", key: "referido" },
        { header: "Motivo Fallido", key: "motivo_fallido" },
        { header: "ID CONTRATO", key: "idContrato" },
      ];

      setExportProgress(60);

      await new Promise(resolve => setTimeout(resolve, 500)); // Dar tiempo para mostrar progreso

      exportToExcel(filteredData, columns, { filters: exportFilters });

      setExportProgress(100);

      // Esperar un momento antes de cerrar el modal
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      setError("Error al exportar a Excel");
    } finally {
      setIsExporting(false);
      setExportType("");
      setExportProgress(0);
    }
  };

  // Exportar a PDF
  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportType("pdf");
    setExportProgress(0);

    try {
      // Simulación de progreso
      setExportProgress(20);

      const columns: ExportColumn[] = [
        { header: "#", key: "clave" },
        { header: "Tipo", key: "tipo" },
        { header: "Vendedor", key: "vendedor" },
        { header: "Area Ventas", key: "cargo" },
        { header: "Fecha Instalación", key: "fecha" },
        { header: "Cliente", key: "nombre_cliente" },
        { header: "Ciudad", key: "ciudad" },
        { header: "Sector", key: "sector" },
        { header: "Zona", key: "zona" },
        { header: "Coordenadas", key: "coordenadas" },
        { header: "Tecnico", key: "tecnico" },
        { header: "Forma Pago", key: "forma_pago" },
        { header: "Banco", key: "Banco" },
        { header: "Estado Venta", key: "estado_venta" },
        { header: "Estado Instalacion", key: "estado_instalacion" },
        { header: "Plan Comercial", key: "nombre_plan" },
        { header: "Plan Tecnico", key: "nombre_plan" },
        { header: "Tipo Plan", key: "tipo_plan" },
        { header: "Costo Mensual", key: "valor_mensual" },
        { header: "Costo Instalación", key: "valor_instalacion" },
        { header: "Servicio Actual", key: "tipo_servicio" },
        { header: "Obs. Adicional", key: "tipo_instalacion" },
        { header: "Equipo", key: "equipo" },
        { header: "Usuario Revisa", key: "usuario_revisa" },
        { header: "Buro", key: "buro" },
        { header: "Segmentación", key: "buro" },
        { header: "Estado Contrato", key: "estado_contrato" },
        { header: "Licenciamiento", key: "licenciamiento" },
        { header: "Medio Publicitario", key: "medio" },
        { header: "Herramienta Utilizada", key: "medio" },
        { header: "Existe Router AC", key: "router_adicional" },
        { header: "Utp Utilizado", key: "detalle_utp" },
        { header: "Promocion Aplicada", key: "detalle_promocion" },
        { header: "Promocion Referido", key: "referido" },
        { header: "Motivo Fallido", key: "motivo_fallido" },
        { header: "ID CONTRATO", key: "idContrato" },
      ];

      setExportProgress(40);

      await new Promise(resolve => setTimeout(resolve, 500));

      setExportProgress(70);

      await exportToPDF(filteredData, columns, { filters: exportFilters });

      setExportProgress(100);

      // Esperar un momento antes de cerrar el modal
      await new Promise(resolve => setTimeout(resolve, 800));
    } catch (error) {
      console.error("Error al exportar PDF:", error);
      setError("Error al exportar a PDF");
    } finally {
      setIsExporting(false);
      setExportType("");
      setExportProgress(0);
    }
  };

  return (
    <Grid container spacing={2.5}>
      {/* Mensaje informativo */}
      <Grid item xs={12}>
        <Alert severity="info" sx={{ bgcolor: "info.light" }}>
          <Typography variant="body2">
            <strong>Información:</strong> Se mostrarán datos de los últimos 31 días.
            Se permite obtener un rango máximo de 31 días por consulta.
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <strong>Nota:</strong> Solo se muestran ventas con estado de instalación "OK" o "Solucionado".
          </Typography>
        </Alert>
      </Grid>

      {/* Error de rango de fechas */}
      {dateRangeError && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setDateRangeError("")}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dateRangeError}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Por favor, ajuste el rango de fechas a máximo 31 días.
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Error con rango permitido */}
      {error && allowedDateRange && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError("")}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {error}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Rango permitido: {allowedDateRange.start} a {allowedDateRange.end}
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Error genérico */}
      {error && !allowedDateRange && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        </Grid>
      )}

      {/* Indicador de carga */}
      {isLoading && (
        <Grid item xs={12}>
          <Box sx={{ width: "100%" }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Cargando datos...
            </Typography>
            <LinearProgress />
          </Box>
        </Grid>
      )}

      {/* Filtros principales */}
      <Grid item xs={12}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DateRangePicker
              size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
              flatpickrRef={flatpickrRef}
              onChange={(range) => onChangeRangeDate(range)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Segmentación de planes</InputLabel>
              <Select
                multiple
                value={planFilter}
                onChange={(e) => {
                  const value = e.target.value as string[];
                  if (value.includes("SELECCIONAR_TODOS")) {
                    setPlanFilter(planFilter.length === PLAN_OPTIONS.length ? [] : PLAN_OPTIONS);
                  } else {
                    setPlanFilter(value);
                  }
                }}
                label="Segmentación de planes"
                renderValue={(selected) =>
                  selected.length === PLAN_OPTIONS.length ? "Todos" : selected.join(", ")
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar plan..."
                    fullWidth
                    value={searchPlan}
                    onChange={(e) => setSearchPlan(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                <MenuItem value="SELECCIONAR_TODOS" dense disabled={searchPlan.trim() !== ""}>
                  <Checkbox checked={planFilter.length === PLAN_OPTIONS.length} size="small" disabled={searchPlan.trim() !== ""} />
                  <ListItemText
                    primary="Seleccionar todos"
                    primaryTypographyProps={{ fontSize: "0.875rem" }}
                  />
                </MenuItem>
                {planesFiltrados.length > 0 ? (
                  planesFiltrados.map((plan) => (
                    <MenuItem key={plan} value={plan} dense>
                      <Checkbox checked={planFilter.indexOf(plan) > -1} size="small" />
                      <ListItemText
                        primary={plan}
                        primaryTypographyProps={{ fontSize: "0.875rem" }}
                      />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled dense>
                    <ListItemText primary="No se encontraron planes" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Canal</InputLabel>
              <Select
                multiple
                value={canalFilter}
                onChange={(e) => {
                  const value = e.target.value as string[];
                  if (value.includes("SELECCIONAR_TODOS")) {
                    setCanalFilter(canalFilter.length === canalesDisponibles.length ? [] : canalesDisponibles);
                  } else {
                    setCanalFilter(value);
                  }
                }}
                label="Canal"
                disabled={isLoadingFiltros || canalesDisponibles.length === 0}
                renderValue={(selected) => selected.length === canalesDisponibles.length ? "Todos" : selected.join(", ")}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <Box sx={{ px: 1.5, py: 0.75, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar canal..."
                    fullWidth
                    value={searchCanal}
                    onChange={(e) => setSearchCanal(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                <MenuItem value="SELECCIONAR_TODOS" dense disabled={searchCanal.trim() !== ""}>
                  <Checkbox checked={canalFilter.length === canalesDisponibles.length} size="small" disabled={searchCanal.trim() !== ""} />
                  <ListItemText primary="Seleccionar todos" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                </MenuItem>
                {canalesFiltrados.length > 0 ? (
                  canalesFiltrados.map((canal: string) => (
                    <MenuItem key={canal} value={canal} dense>
                      <Checkbox checked={canalFilter.indexOf(canal) > -1} size="small" />
                      <ListItemText primary={canal} primaryTypographyProps={{ fontSize: '0.875rem' }} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled dense>
                    <ListItemText primary="No se encontraron canales" primaryTypographyProps={{ fontSize: '0.875rem' }} />
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md="auto" lg="auto" sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Recargar Canales">
              <IconButton
                color="primary"
                onClick={handleRefreshFiltros}
                disabled={isLoadingFiltros}
                size="small"
                sx={{
                  border: 1,
                  borderColor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                    borderColor: 'primary.dark'
                  },
                  '&.Mui-disabled': {
                    borderColor: 'action.disabled'
                  }
                }}
              >
                {isLoadingFiltros ? <CircularProgress size={20} /> : <Refresh />}
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>

      {/* Botones de acción */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            startIcon={<Search />}
            onClick={handleSearch}
            disabled={dateRangeError !== ""}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Consultar
          </Button>

          <Button
            variant="outlined"
            color="primary"
            size="medium"
            startIcon={<FilterAltOffIcon />}
            onClick={handleCleanFilter}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Limpiar Filtros
          </Button>

          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />

          <Button
            variant="contained"
            color="success"
            size="medium"
            startIcon={<Description />}
            onClick={handleExportExcel}
            disabled={allData.length === 0}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            Excel
          </Button>

          <Button
            variant="contained"
            color="error"
            size="medium"
            startIcon={<PictureAsPdf />}
            onClick={handleExportPDF}
            disabled={allData.length === 0}
            sx={{
              flex: { xs: 1, sm: '0 1 auto' },
              minWidth: { xs: 'auto', sm: 140 },
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2,
              '&:hover': { boxShadow: 4 }
            }}
          >
            PDF
          </Button>
        </Box>
      </Grid>

      {/* Información de registros */}
      {!isLoading && allData.length > 0 && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ fontWeight: 500 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {filteredData.length === allData.length ? (
                `Total de registros encontrados: ${allData.length.toLocaleString()}`
              ) : (
                `Mostrando ${filteredData.length.toLocaleString()} de ${allData.length.toLocaleString()} registros`
              )}
            </Typography>
          </Alert>
        </Grid>
      )}

      {/* Tabla */}
      <Grid item xs={12}>
        <GeneralTable
          columns={columnBase}
          data={paginatedData}
          isLoading={isLoading}
          pagination={pagination}
          onPaging={setPagination}
          rowCount={filteredData.length}
          enableColumnActions={false}
          enableEditing={false}
          enableGlobalFilter={false}
          enableColumnFilters={false}
        />
      </Grid>

      {/* Modal de progreso de exportación */}
      <Dialog
        open={isExporting}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            minWidth: 400,
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            {exportType === "excel" ? (
              <Description sx={{ fontSize: 48, color: 'success.main' }} />
            ) : (
              <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Exportando {exportType === "excel" ? "Excel" : "PDF"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                {exportProgress < 50 ? "Preparando datos..." :
                 exportProgress < 80 ? "Generando archivo..." :
                 "Finalizando descarga..."}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={exportProgress}
              sx={{ height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {exportProgress}% completado
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              Por favor espere, esto puede tomar unos segundos...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
